/**
 * PRO Response domain types + week-label helpers.
 *
 * Real per-week grade/response data comes from the medical-api adapter
 * (`fetchProHeatmap` / `fetchProCellDetail` in `api/adapters/patients.ts`); the prototype's
 * deterministic mock generator was removed once that adapter went live.
 */

export type ProGrade = 1 | 2 | 3;

/**
 * How this app draws an answer. The platform's own answer-type codes are the eight in the cross-app contract
 * (`SHORT`/`MULTI`/`YESNO`/`OX`/`TEXT`/`SLIDER`/`PHOTO`/`BATTERY`); those are the CODES on the wire. This is the
 * smaller set of *renderings* they map onto — several codes are drawn the same way (a Yes/No and an O/X are both a
 * single chosen option). The wire code is never re-spelled; it is translated here, in one place.
 */
export type ProAnswerRender = "single" | "multi" | "text" | "slider" | "battery";

/**
 * ONE answered question inside a PRO item on one day.
 *
 * A PRO item is a set of questions, not a single question — the day's grade is the sum of their scores. Showing
 * only one of them would silently drop the rest of what the patient actually told us, so a day carries every
 * answer it has.
 */
export interface ProDayAnswer {
	/** The administrator's question id — so two answers to the same item are distinguishable. */
	questionQid: string;
	/** Which rendering this answer gets. Defaults to `single` when the server sends a type this build cannot draw. */
	answerType: ProAnswerRender;
	/** The option label the patient chose, exactly as stored. Never re-assembled or reformatted. */
	answerText: string;
	/**
	 * text: the body the patient wrote, which the requirement keeps separate from `otherText` — one is the whole
	 * answer, the other a remark attached to a chosen option. Empty for every other answer type.
	 */
	textValue: string;
	/** The patient's free-text '기타' remark. Always empty on a written answer, whose body is `textValue`. */
	otherText: string;
	/** This question's own score, when it scores. */
	score?: number;
	/** Photos the patient attached to this answer. */
	photos?: { url: string; caption?: string }[];

	// ── The richer per-type shapes the popover draws. ──
	//
	// `multi`, `sliderValue`/`sliderUnit`/range and `battery` all come from the server, including the two that
	// used to arrive blank: `MedicalProService` now reads each answered question's admin config in one batched
	// pass, so a slider's unit/min/max and a battery sub-item's NAME are sourced from the admin master rather
	// than being left null. They stay optional because a question whose config was never filled in still has
	// none — the renderer then degrades to the stored answer label rather than inventing a range or a position
	// number.
	/**
	 * multi: the selected option labels, as chips.
	 *
	 * Built by the adapter from the server's one-row-per-selection answers, which share a `questionQid` —
	 * see `groupMultiSelections`. Nothing is split: the labels arrive already separate.
	 */
	multi?: string[];
	/** slider: the value, its unit, and the range the gauge is drawn against. */
	sliderValue?: number;
	sliderUnit?: string;
	sliderMin?: number;
	sliderMax?: number;
	/** battery (묶음): each sub-item's name and scale value, plus their average. */
	battery?: { label: string; value: number }[];
	batteryAvg?: number;
}

/** Per-day response within a week. */
export interface ProDayResponse {
	/** Korean day-of-week label (월요일, 화요일, …). */
	label: string;
	/** ISO date string. */
	date: string;
	/** Whether this day is a scheduled response day. */
	isExpected: boolean;
	/** Day status: responded | missed | future-expected. */
	status: "responded" | "missed" | "future-expected";
	/** Grade level (null = missed/future). */
	level: ProGrade | null;
	/** Numeric grade score for this response (PD-02 §3.2 Response Detail); undefined until BE-1. */
	score?: number;
	/**
	 * Every question of this item the patient answered that day — the server's answer, and what the popover draws.
	 * Empty on a missed or future day. Optional only because the legacy mock generator below predates it.
	 */
	answers?: ProDayAnswer[];
	/** Abbreviated answer text (used by single/text answer types). */
	answerText: string;
	/** Free-text / other remark. */
	otherText: string;
	// ── PD-02 v2.2: answerType-based response rendering (all optional; from BE-D2) ──
	/** Response answer type; defaults to "single" when absent. */
	answerType?: "single" | "multi" | "text" | "slider" | "battery";
	/** multi: selected option labels. */
	multi?: string[];
	/** slider: value + unit + range for the gauge. */
	sliderValue?: number;
	sliderUnit?: string;
	sliderMin?: number;
	sliderMax?: number;
	/** battery (묶음): sub-items (항목·척도값) + average. */
	battery?: { label: string; value: number }[];
	batteryAvg?: number;
	// ── PD-02 v2.1: attached photos (from BE-D3; signed URLs in prod) ──
	photos?: { url: string; caption?: string }[];
}

/**
 * Week status.
 *
 * `completed` means the patient DID answer, but the item is pass/fail and has no score to give (the
 * self-efficacy questions work this way). It is deliberately NOT folded into `responded` with a null grade:
 * a null grade renders the missed-chip, so a patient who answered would be drawn as one who did not — the
 * exact confusion this grid exists to prevent. See TBD_MEDICAL_PRO_CELL_STATE_COMPLETED_NOT_IN_SPEC_01.
 */
export type ProWeekStatus =
	| "responded"
	| "completed"
	| "missed"
	| "partial"
	| "future-expected"
	| "none"
	| "future";

export interface ProWeekResponse {
	/** Aggregated grade for the week (null = missed / not recorded). */
	grade: ProGrade | null;
	/** Week-level status. */
	status: ProWeekStatus;
	/** Average numeric score (0–100). */
	score: number;
	/** Free-text note from clinician. */
	note: string;
	/** ISO date of last entry in the week. */
	date: string;
	/** Whether recording was delayed > 2 days. */
	delayed: boolean;
	/** Week start date (ISO). */
	weekStart: string;
	/** Week end date (ISO). */
	weekEnd: string;
	/** Week number (1-based). */
	week: number;
	/** Day-level breakdown. */
	days: ProDayResponse[];
	/** For daily items — number of daily entries aggregated. */
	dailyCount?: number;
}

/** Map key: `${itemId}:${weekIndex}` */
export type ProResponseMap = Map<string, ProWeekResponse>;

/** Get week date label for display (e.g. "1월 3주"). */
export function getWeekLabel(weekIndex: number, baseDate?: Date): string {
	const base =
		baseDate ??
		new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1);
	const d = new Date(base);
	d.setDate(d.getDate() + weekIndex * 7);
	const month = d.getMonth() + 1;
	// ISO week-of-month approximation
	const dayOfMonth = d.getDate();
	const weekOfMonth = Math.ceil(dayOfMonth / 7);
	return `${month}월 ${weekOfMonth}주`;
}

/** Get compact date label for week header (e.g. "3/9"). */
export function getWeekDateRange(weekIndex: number, baseDate?: Date): string {
	const base =
		baseDate ??
		new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1);
	const start = new Date(base);
	start.setDate(start.getDate() + weekIndex * 7);
	const end = new Date(start);
	end.setDate(end.getDate() + 6);

	const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
	return `${fmt(start)}–${end.getDate().toString()}`;
}

/** Get full date range for tooltips (e.g. "2026-04-27~2026-05-03"). */
export function getWeekDateRangeFull(
	weekIndex: number,
	baseDate?: Date,
): string {
	const base =
		baseDate ??
		new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1);
	const start = new Date(base);
	start.setDate(start.getDate() + weekIndex * 7);
	const end = new Date(start);
	end.setDate(end.getDate() + 6);

	const fmt = (d: Date) =>
		`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	return `${fmt(start)}~${fmt(end)}`;
}
